/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { handleError } from '@/utils/handelErrorMessage';
import componentsArr from 'data/components-library.json';
import { toast } from 'sonner';
import { Component } from 'types/api-data';
import { OptionsTypes } from 'types/multi-select';

import { useComponents } from '@/hooks/useComponents';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Logo } from '@/components/cards';
import { EnhancedMultiSelect } from '@/components/combobox';
import { Icons } from '@/components/icons';

const WorkflowFramework = ({ workflowName }: { workflowName: string }) => {
  const [selectedFrameworks, setSelectedFrameworks] = useState<OptionsTypes>(
    []
  );
  const [frameworkLoading, setFrameworkLoading] = useState(false);
  const [frameworkLoadingDropDown, setFrameworkLoadingDropDown] =
    useState(false);
  const { messages } = useLanguage();
  const compData = messages?.ComponentsPage;

  const {
    state: { frameworks, components },
    componentsActions,
  } = useComponents();

  const findComp = components?.find(
    (comp: Component) => comp?.component_name === workflowName
  );

  const frameworkTransformedArr: OptionsTypes =
    frameworks?.map((framework: string, index: number) => {
      const match = componentsArr.components.find(
        (item) => item.type === framework
      );
      return {
        id: index + 1,
        label: match ? match.component_display_name : framework,
        value: framework,
        iconType: framework,
      };
    }) || []; // Added fallback for undefined frameworks

  // Set selectedFrameworks based on framework values directly
  useEffect(() => {
    if (!findComp) return;
    const currentIncludedTypes =
      findComp?.properties?.included_workflow_types ?? [];
    const findType = componentsArr.components.find(
      (item) => item.type === findComp?.type
    );
    let newSelectedFrameworks: OptionsTypes = [];

    if (currentIncludedTypes.length > 0) {
      newSelectedFrameworks = currentIncludedTypes.map(
        (fw: string, index: number) => {
          const match = componentsArr.components.find(
            (item) => item.type === fw
          );
          return {
            id: index + 1,
            label: match ? match.component_display_name : fw,
            value: fw,
            iconType: fw,
          };
        }
      );
    } else if (
      findComp.type &&
      findType?.type !== 'workflow.generic' &&
      findType?.type !== 'workflow.hybrid'
    ) {
      // If no included_workflow_types, but component.type is a specific workflow,
      // then that type itself is considered the selected framework.
      if (findType) {
        newSelectedFrameworks = [
          {
            id: 1,
            label: findType.component_display_name ?? '',
            value: findType.type ?? '',
            iconType: findType.type ?? '',
          },
        ];
      }
    }
    setSelectedFrameworks(newSelectedFrameworks);
  }, [findComp?.properties?.included_workflow_types, findComp?.type]); // Depend on the specific parts of component

  // Create the default value for EnhancedMultiSelect
  const multiSelectDefaultValue =
    selectedFrameworks?.map((item) => item.value) || [];

  const fetchFrameworks = async () => {
    // Only fetch if frameworks haven't been loaded yet
    if (!frameworks || frameworks.length === 0) {
      try {
        setFrameworkLoadingDropDown(true);
        await componentsActions.fetchFrameworks();
      } catch (error) {
        console.log(error);
      } finally {
        setFrameworkLoadingDropDown(false);
      }
    }
  };

  const handleFrameworkChange = (selectedValues: string[]) => {
    // Find the full objects from frameworkTransformedArr for the selected values
    const selected = frameworkTransformedArr.filter((item) =>
      selectedValues.includes(item.value)
    );
    setSelectedFrameworks(selected);
    // Compare with the frameworks currently derived from the component prop
    const currentPropFrameworkValues =
      findComp?.properties?.included_workflow_types ?? [];
    let baseFrameworksForComparison = currentPropFrameworkValues;
    const findType = componentsArr.components.find(
      (item) => item.type === findComp.type
    );

    if (
      currentPropFrameworkValues.length === 0 &&
      findComp.type &&
      findType &&
      findType.type !== 'workflow.generic' &&
      findType.type !== 'workflow.hybrid'
    ) {
      baseFrameworksForComparison = [findComp.type];
    }

    if (
      !checkFrameworksMatch(baseFrameworksForComparison, selected).isMatching
    ) {
      updateFrameworks(selected);
    }
  };

  function checkFrameworksMatch(
    framework: string[],
    selectedFrameworks: OptionsTypes
  ): { isMatching: boolean; selectedValues: string[] } {
    // Extract values from selectedFrameworks array
    const selectedValues = selectedFrameworks.map((item) => item.value);

    // Check if both arrays have the same length
    const isMatching =
      framework.length === selectedValues.length &&
      framework.every((item) => selectedValues.includes(item)) && // All items from framework are in selectedValues
      selectedValues.every((item) => framework.includes(item));
    return {
      isMatching,
      selectedValues,
    };
  }

  const inferFrameworkType = (frameworks: OptionsTypes) => {
    if (frameworks.length >= 2) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Hybrid Workflow'
      )?.type;
    } else if (frameworks.length === 1) {
      return frameworks[0].value;
    } else if (frameworks.length === 0) {
      return componentsArr.components.find(
        (item) => item.component_display_name === 'Generic Workflow'
      )?.type;
    }
  };

  const updateFrameworks = async (frameworksToUpdate: OptionsTypes) => {
    const { component_name } = findComp;
    const payload = {
      type: inferFrameworkType(frameworksToUpdate), // Pass the updated frameworks directly
      domain: 'logical',
      component_name,
      status: 'active',
      properties: {
        ...findComp?.properties,
        included_workflow_types:
          frameworksToUpdate.length >= 2
            ? frameworksToUpdate.map((item) => item.value)
            : undefined,
      },
    };
    try {
      setFrameworkLoading(true);
      await componentsActions.updateComponent(component_name, payload);
      await componentsActions.fetchComponents(false);
      toast.success(compData?.component_updated_successfully);
    } catch (error) {
      console.error('Error in updating component:', error);
      handleError(error, compData?.error_in_updating_component);
    } finally {
      setFrameworkLoading(false);
    }
  };

  return (
    <div>
      {frameworkLoading ? (
        <div className="min-w-48 border-l border-[#40565A] pl-4">
          <Skeleton className="animate-pulse-custom h-[50px] w-44 rounded-xl bg-[#8C9EA1]" />
        </div>
      ) : (
        <div className="flex min-w-44 border-l border-[#40565A] pl-4">
          {selectedFrameworks?.length > 0 ? (
            <div className="flex flex-col">
              <h3 className="pb-1 text-body-sm text-form-input-disabled">
                {compData?.framework_or_api}
              </h3>
              <div className="group flex items-center gap-2">
                <div className="flex gap-2">
                  {selectedFrameworks.map((item) => (
                    <TooltipProvider key={item.value}>
                      <Tooltip>
                        <TooltipTrigger>
                          <Logo
                            type={item.value}
                            size={{ img: 'size-6', bg: 'size-8' }}
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="rounded border-transparent bg-[#07181E]"
                        >
                          {item.label}
                          <TooltipArrow className="fill-[#07181E]" />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>

                <div className="hidden group-hover:block">
                  <EnhancedMultiSelect
                    asChild
                    defaultValue={multiSelectDefaultValue}
                    showPills={false}
                    loading={frameworkLoadingDropDown}
                    options={frameworkTransformedArr}
                    onValueChange={handleFrameworkChange}
                    onOpenChange={(isOpen) => {
                      if (isOpen) {
                        fetchFrameworks();
                      }
                    }}
                    key={multiSelectDefaultValue.join(',')} // Force re-render when default values change
                  >
                    <button
                      className="rounded-[4px] bg-[#257C9D0F] p-1"
                      type="button"
                    >
                      {Icons.Edit('size-4')}
                      <span className="sr-only">Pencil Icon</span>
                    </button>
                  </EnhancedMultiSelect>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <h3 className="self-start text-body-sm text-form-input-disabled">
                {compData?.framework_or_api}
              </h3>
              <div>
                <EnhancedMultiSelect
                  asChild
                  defaultValue={multiSelectDefaultValue}
                  showPills={false}
                  loading={frameworkLoadingDropDown}
                  options={frameworkTransformedArr}
                  onValueChange={handleFrameworkChange}
                  onOpenChange={(isOpen) => {
                    if (isOpen) {
                      fetchFrameworks();
                    }
                  }}
                >
                  <button
                    className="inline-flex cursor-pointer items-center gap-1 text-link-active"
                    type="button"
                  >
                    {Icons.Plus('stroke-0')}
                    <span className="text-sm">{compData?.add_framework}</span>
                  </button>
                </EnhancedMultiSelect>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowFramework;
